import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import {
  downloadTimingRecommendedWindowsXlsx,
  fetchTimingRecommendedWindows,
} from '../../services/contactOptimizationService';
import { useRefreshRegistration } from './RefreshController';
import { formatDateRange } from '../../utils/date';
import type {
  TimingRecommendedWindowRange,
  TimingRecommendedWindows,
  TimingRecommendedWindowsStateRow,
} from '../../types/contactOptimization';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import ChartSectionHeader from './ChartSectionHeader';
import ClockBadge from './ClockBadge';
import { SectionScopeChip } from './ScopeBanner';
import { buildContactOptGridSx, CONTACT_OPT_PAGE_SIZE_OPTIONS } from './contactOptGridKit';
import { BAND_COLORS, BAND_TEXT_COLORS, BAND_LABELS, pct, ratioX } from './timingCharts';

// How many states are shown on first load -- same "top by opportunity"
// default the heatmap and Compare-states chart above already use, so a
// visitor sees a consistent, small starting point instead of all ~51
// states at once. "Opportunity" order is decided once, server-side (see
// service.py's all_states_by_opportunity), so this is just "the first 3
// rows the API gave us," not a second ranking invented here.
const DEFAULT_STATE_COUNT = 3;

const REASON_LABELS: Record<string, string> = {
  connect_rate: 'Connect rate',
  value_cost_ratio: 'Value/Cost Ratio',
};

type TimezoneField = 'hour_label' | 'eastern_hour_label' | 'central_hour_label' | 'mountain_hour_label' | 'pacific_hour_label';

interface StateGridRow extends TimingRecommendedWindowsStateRow {
  id: string;
  reasons_label: string;
}

// Plain styled pills, not <Chip> -- the global MuiChip theme override
// (`&.MuiChip-colorDefault`) has higher CSS specificity than a plain
// `sx={{ bgcolor }}` on a color-less Chip, and silently washes any custom
// background out to grey. Same issue/fix as SettingsPage's SemanticPill --
// a styled <Box component="span"> sidesteps the theme override entirely
// instead of fighting it.
function Pill({ label, bg, fg }: { label: string; bg: string; fg: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: 999,
        bgcolor: bg,
        color: fg,
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        maxWidth: '100%',
      }}
    >
      {label}
    </Box>
  );
}

function BandChip({ band }: { band: 'prime' | 'good' }) {
  return <Pill label={BAND_LABELS[band]} bg={BAND_COLORS[band]} fg={BAND_TEXT_COLORS[band]} />;
}

/**
 * One colored badge per recommended window, stacked vertically -- so a
 * state with three separate windows ("7am-9am", "11am-4pm", "6pm-7pm")
 * reads as three clearly separated pills instead of one truncated,
 * comma-packed string. The color identifies the *column* (which clock
 * this time is in), not the band -- every badge in the Eastern column is
 * the same color, whether that state's window is Prime or Good, so the
 * color always answers "which timezone am I looking at," consistently
 * across every row.
 */
function TimezoneBadges({
  windows,
  field,
  colors,
}: {
  windows: TimingRecommendedWindowRange[];
  field: TimezoneField;
  colors: { bg: string; fg: string };
}) {
  return (
    <Stack spacing={0.5} sx={{ py: 0.75 }}>
      {windows.map((w, i) => (
        <Pill key={i} label={w[field] ?? '\u2013'} bg={colors.bg} fg={colors.fg} />
      ))}
    </Stack>
  );
}

function buildAllStatesCsv(data: TimingRecommendedWindows): string {
  const header = [
    'state', 'windows_local', 'windows_eastern', 'windows_central', 'windows_mountain', 'windows_pacific',
    'band', 'why_it_qualifies', 'state_connect_rate', 'state_value_cost_ratio', 'avg_confidence_pct', 'per_hour_confidence', 'total_calls',
  ];
  const lines = [header.join(',')];
  const csvField = (v: string) => `"${v.replace(/"/g, '""')}"`;
  for (const row of data.states) {
    lines.push(
      [
        row.state,
        csvField(row.windows_label),
        csvField(row.eastern_windows_label),
        csvField(row.central_windows_label),
        csvField(row.mountain_windows_label),
        csvField(row.pacific_windows_label),
        row.band,
        csvField(row.reasons.map((r) => REASON_LABELS[r] ?? r).join(' & ')),
        row.connect_rate?.toFixed(4) ?? '',
        row.value_cost_ratio?.toFixed(4) ?? '',
        row.avg_confidence_pct?.toFixed(4) ?? '',
        csvField(row.hour_detail_label),
        row.total_calls,
      ].join(','),
    );
  }
  return lines.join('\n');
}

function downloadAllStatesCsv(data: TimingRecommendedWindows) {
  const blob = new Blob([buildAllStatesCsv(data)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'recommended-calling-windows.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function RecommendedWindowsGridToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 2, py: 1, gap: 1.5, borderBottom: 1, borderColor: 'divider', justifyContent: 'flex-end' }}>
      <Box sx={{ width: { xs: '100%', sm: 240 } }}>
        <GridToolbarQuickFilter debounceMs={250} />
      </Box>
    </GridToolbarContainer>
  );
}

/**
 * The recommended-windows rollup (CC-PROTOTYPE-AUDIT.md gap #2): a short,
 * actionable "which hours to call" list -- one row per state, with hours
 * collapsed into ranges, matching the reference prototype's own output
 * shape (`cc_engine2.py::build_recommendations_with_confidence`). Fetches
 * on its own (a separate, cheaper endpoint than the full heatmap) rather
 * than being derived client-side, so the rollup logic lives in exactly
 * one place.
 *
 * Only the top few states show by default -- the full 51-state list is
 * always fetched (so the CSV/Excel downloads and "add a state" picker
 * both cover everything), but showing all of them at once buries the
 * states that matter most. "Add a state" brings in more without losing
 * that starting focus.
 */
export default function RecommendedWindowsSection() {
  const theme = useTheme();
  const gridSx = React.useMemo(() => buildContactOptGridSx(theme), [theme]);
  const [data, setData] = React.useState<TimingRecommendedWindows | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadAnchor, setDownloadAnchor] = React.useState<null | HTMLElement>(null);
  const [showHelp, setShowHelp] = React.useState(false);
  const [selectedStates, setSelectedStates] = React.useState<string[]>([]);

  const tzColors = React.useMemo(
    () => ({
      local: { bg: alpha(theme.palette.primary.main, 0.14), fg: theme.palette.primary.dark },
      eastern: { bg: alpha(theme.palette.success.main, 0.16), fg: theme.palette.success.dark },
      central: { bg: alpha(theme.palette.warning.main, 0.2), fg: theme.palette.warning.dark },
      mountain: { bg: alpha(theme.palette.secondary.main, 0.16), fg: theme.palette.secondary.dark },
      pacific: { bg: alpha(theme.palette.info.main, 0.18), fg: theme.palette.info.dark },
    }),
    [theme],
  );

  const load = React.useCallback((opts?: { refresh?: boolean }) => {
    setLoading(true);
    setError(null);
    return fetchTimingRecommendedWindows(opts)
      .then((result) => {
        setData(result);
        // Only reset the picked states on the very first load -- a
        // refresh should update every state's numbers in place, not
        // silently reset whatever the user has added/removed since.
        setSelectedStates((prev) => (prev.length > 0 ? prev : (result.states ?? []).slice(0, DEFAULT_STATE_COUNT).map((s) => s.state)));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load recommended calling windows'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRefreshRegistration(React.useCallback(() => load({ refresh: true }), [load]));

  const allRows: StateGridRow[] = React.useMemo(() => {
    // Defensive: a payload can come back from a stale Postgres cache row
    // computed by an older version of this endpoint (TTL is several
    // hours, see cache.py) whose shape doesn't match what this component
    // expects. Never let that crash the page -- fall back to blanks for
    // that row instead of throwing on `.map` of undefined.
    if (!data?.states) return [];
    return data.states.map((stateRow) => ({
      ...stateRow,
      id: stateRow.state,
      reasons_label: (stateRow.reasons ?? []).map((r) => REASON_LABELS[r] ?? r).join(' & '),
    }));
  }, [data]);

  const rows = React.useMemo(() => {
    const selected = new Set(selectedStates);
    return allRows.filter((r) => selected.has(r.state));
  }, [allRows, selectedStates]);

  const stateOptions = React.useMemo(() => allRows.map((r) => r.state), [allRows]);

  const columns: GridColDef<StateGridRow>[] = React.useMemo(
    () => [
      // `display: 'flex'` on every column -- without it, `.MuiDataGrid-cell`
      // is a plain block box (only `.cell--flex`, the class this prop turns
      // on, actually gets `display: flex; alignItems: center`), so with
      // dynamic (`auto`) row height enabled for the taller badge-stack
      // columns, every cell's content sat at the top of its row instead of
      // centering -- a global `alignItems: center` override on `.cell`
      // itself does nothing when the element was never a flex container to
      // begin with.
      { field: 'state', headerName: 'State', width: 96, display: 'flex' },
      {
        field: 'windows_label',
        headerName: 'Local time',
        description: "Recommended calling windows in the lead's own local time",
        width: 160,
        sortable: false,
        display: 'flex',
        renderCell: (params) => <TimezoneBadges windows={params.row.windows} field="hour_label" colors={tzColors.local} />,
      },
      {
        field: 'eastern_windows_label',
        headerName: 'Eastern',
        description: "The same recommended calling windows as the Local time column, converted into Eastern Time — useful if your team is scheduling from an Eastern Time clock instead of each lead's own local time.",
        width: 150,
        sortable: false,
        display: 'flex',
        renderCell: (params) => (
          <TimezoneBadges windows={params.row.windows} field="eastern_hour_label" colors={tzColors.eastern} />
        ),
      },
      {
        field: 'central_windows_label',
        headerName: 'Central',
        description: "The same recommended calling windows as the Local time column, converted into Central Time — useful if your team is scheduling from a Central Time clock instead of each lead's own local time.",
        width: 150,
        sortable: false,
        display: 'flex',
        renderCell: (params) => (
          <TimezoneBadges windows={params.row.windows} field="central_hour_label" colors={tzColors.central} />
        ),
      },
      {
        field: 'mountain_windows_label',
        headerName: 'Mountain',
        description: "The same recommended calling windows as the Local time column, converted into Mountain Time — useful if your team is scheduling from a Mountain Time clock instead of each lead's own local time.",
        width: 150,
        sortable: false,
        display: 'flex',
        renderCell: (params) => (
          <TimezoneBadges windows={params.row.windows} field="mountain_hour_label" colors={tzColors.mountain} />
        ),
      },
      {
        field: 'pacific_windows_label',
        headerName: 'Pacific',
        description: "The same recommended calling windows as the Local time column, converted into Pacific Time — useful if your team is scheduling from a Pacific Time clock instead of each lead's own local time.",
        width: 150,
        sortable: false,
        display: 'flex',
        renderCell: (params) => (
          <TimezoneBadges windows={params.row.windows} field="pacific_hour_label" colors={tzColors.pacific} />
        ),
      },
      {
        field: 'band',
        headerName: 'Band',
        description: '"Prime" means this state\u2019s best hours clearly beat its own average; "Good" means they beat it, but by a smaller margin. Only states with at least one Prime or Good hour appear in this list.',
        width: 90,
        display: 'flex',
        renderCell: (params) => <BandChip band={params.value as 'prime' | 'good'} />,
      },
      { field: 'reasons_label', headerName: 'Why it qualifies', description: 'Which measurement(s) this state\u2019s recommended hours beat its own average on: connect rate, Value/Cost Ratio, or both.', width: 190, display: 'flex' },
      {
        field: 'connect_rate',
        headerName: "State's connect rate",
        description: "This state's own overall share of calls that connected for 90+ seconds, across all hours — not specific to any one recommended hour.",
        width: 140,
        display: 'flex',
        valueFormatter: (value: number | null) => pct(value, 1),
      },
      {
        field: 'value_cost_ratio',
        headerName: "State's Value/Cost Ratio",
        description: 'Dollars of paid premium expected back per dollar spent dialing, averaged across this whole state — the same metric as the heatmap above, just rolled up to one number per state',
        width: 170,
        display: 'flex',
        valueFormatter: (value: number | null) => ratioX(value, 1),
      },
      {
        field: 'avg_confidence_pct',
        headerName: 'Avg. confidence',
        description: "How sure we are that this state's recommended hours are a real, lasting pattern rather than luck, averaged across all its qualifying hours. Higher means more sure. See Per-hour confidence for the number behind each individual hour.",
        width: 130,
        display: 'flex',
        valueFormatter: (value: number | null) => pct(value, 0),
      },
      {
        field: 'hour_detail_label',
        headerName: 'Per-hour confidence',
        description: "Every individual qualifying hour's own confidence — not just the state's average",
        width: 320,
        sortable: false,
        display: 'flex',
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8125rem', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.6, py: 0.75 }}
          >
            {params.value as string}
          </Typography>
        ),
      },
      { field: 'total_calls', headerName: 'Calls this state is based on', description: 'Total number of historical calls to this state, across all hours, used to compute every other number in this row. More calls means the numbers above are more reliable.', width: 190, type: 'number', display: 'flex' },
    ],
    [tzColors],
  );

  const closeDownloadMenu = () => setDownloadAnchor(null);

  const handleDownloadXlsx = async () => {
    closeDownloadMenu();
    setDownloading(true);
    try {
      await downloadTimingRecommendedWindowsXlsx();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to download workbook');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = () => {
    closeDownloadMenu();
    if (data) downloadAllStatesCsv(data);
  };

  return (
    <Paper
      elevation={0}
      sx={(t) => ({ p: 2, mb: 2, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px` })}
    >
      <ChartSectionHeader
        title="Recommended calling windows, by state"
        subtitle={
          data
            ? `The short, actionable list of specific hours worth calling, per state. ${allRows.length} state${allRows.length === 1 ? '' : 's'} have at least one${
                data.states_with_no_recommended_hour > 0
                  ? ` \u00b7 ${data.states_with_no_recommended_hour} state${data.states_with_no_recommended_hour === 1 ? '' : 's'} have none right now`
                  : ''
              } \u00b7 showing ${rows.length} below`
            : undefined
        }
        actions={
          <>
            <SectionScopeChip
              window={{
                label: 'Full call history',
                detail: `${formatDateRange(data?.date_range.min_call_dt, data?.date_range.max_call_dt) || '\u2013'} \u00b7 every lead, channel, product`,
              }}
            />
            <ClockBadge basis="lead_local" />
            <Tooltip title={downloading ? 'Preparing download…' : 'Download all states as CSV or Excel'} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => setDownloadAnchor(e.currentTarget)}
                  disabled={downloading || loading || !data}
                  aria-label="Download"
                  aria-haspopup="menu"
                  sx={{ width: 'auto', px: 0.5 }}
                >
                  {downloading
                    ? <CircularProgress size={16} />
                    : (
                      <>
                        <DownloadRounded sx={{ fontSize: 16 }} />
                        <ArrowDropDownRounded sx={{ fontSize: 16, ml: -0.125, opacity: 0.7 }} />
                      </>
                    )}
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={closeDownloadMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleDownloadCsv} sx={{ fontSize: '0.8125rem' }}>
                Download as CSV
              </MenuItem>
              <MenuItem onClick={handleDownloadXlsx} sx={{ fontSize: '0.8125rem' }}>
                Download as Excel (.xlsx)
              </MenuItem>
            </Menu>
            <ExplainerToggle title="How this list is built" open={showHelp} onClick={() => setShowHelp((v) => !v)} />
          </>
        }
      />

      <ExplainerNote title="How this list is built" open={showHelp}>
        {`This turns the heatmap above into a short, actionable list: **which hours are actually worth calling, per state**, one row per state instead of one row per hour.

- An hour makes a state's list if it's **Prime or Good**: it beats that state's own average, with statistical confidence, on connect rate **or** on Value/Cost Ratio. A state with no qualifying hour isn't listed at all.
- Qualifying hours are **collapsed into windows** — three straight qualifying hours read as one badge ("9am\u201312pm"), not three rows. A state can show more than one badge per column if its good hours aren't all consecutive (e.g. "9am\u201311am" and "2pm\u20134pm" as two separate badges).
- Each timezone column is its own color. The color tells you which clock you're reading, not whether that window is Prime or Good (that's the separate **Band** column).
- **State's connect rate** and **State's Value/Cost Ratio** are that state's own overall numbers, not specific to any one hour, so you can see the state's general opportunity alongside which hours make its shortlist.
- **Avg. confidence** is the average, across a state's qualifying hours, of the probability that hour's true rate is genuinely above the state's own average, not just a lucky sample. Hover **Per-hour confidence** to see every individual hour's own number.
- Only the top ${DEFAULT_STATE_COUNT} states (by opportunity, same ranking the heatmap and "Compare states" chart above use) show at first. Use **Add a state** above the table to bring in more. The **CSV** and **Excel** downloads (menu above) always cover all ${allRows.length} states, regardless of what's shown on screen.
- Eastern/Central/Mountain/Pacific columns are approximate: each state is mapped to its single predominant time zone (a state that straddles two zones uses whichever one holds more of its population), and Alaska/Hawaii use an approximate year-round offset.`}
      </ExplainerNote>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Which states to show — second header row, left-aligned above the table. */}
      {!loading && data && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={1}
          sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            States shown:
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={stateOptions}
            value={selectedStates}
            onChange={(_e, newValue) => setSelectedStates(newValue)}
            renderInput={(params) => <TextField {...params} placeholder={'Add a state\u2026'} />}
            sx={{ minWidth: 260, maxWidth: 480 }}
          />
          {selectedStates.length < allRows.length && (
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => setSelectedStates(allRows.map((r) => r.state))}
            >
              Show all {allRows.length} states
            </Link>
          )}
          {selectedStates.length > DEFAULT_STATE_COUNT && (
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => setSelectedStates(allRows.slice(0, DEFAULT_STATE_COUNT).map((r) => r.state))}
            >
              Reset to top {DEFAULT_STATE_COUNT}
            </Link>
          )}
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        // `autoHeight` -- not a fixed pixel height -- so the grid's own
        // height always matches its actual (variable, wrapped-badge) row
        // heights exactly. A fixed height here (the previous approach)
        // left a dead strip of blank space between the last row and the
        // pagination footer whenever real rows rendered shorter than the
        // estimate.
        <Box sx={{ width: '100%', border: (t) => `1px solid ${t.tokens.border.subtle}`, borderRadius: (t) => `${t.ds.radius.sm}px`, overflow: 'hidden' }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            getRowHeight={() => 'auto'}
            getEstimatedRowHeight={() => 90}
            disableRowSelectionOnClick
            sortingMode="client"
            paginationMode="client"
            pageSizeOptions={CONTACT_OPT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
              sorting: { sortModel: [{ field: 'state', sort: 'asc' }] },
            }}
            slots={{ toolbar: RecommendedWindowsGridToolbar }}
            localeText={{ toolbarQuickFilterPlaceholder: 'Search state\u2026' }}
            sx={gridSx}
          />
        </Box>
      )}
    </Paper>
  );
}
